/* eslint-disable import/order */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Form, Row, Col, Button, Modal } from 'react-bootstrap';
import { Group, GroupService, InvitationRead, InvitationService } from '../../../../api';
import { Pagination } from '../../../../app/components/Pagination/Pagination';
import { useSearchParamsState } from '../../../../app/hooks/search-params';
import 'react-range-slider-input/dist/style.css';
import styles from './PendingInvitationsList.module.scss';
import { Card } from '../../../../app/components/Card';
import { BsPersonLock, BsSendPlus, BsTrash } from 'react-icons/bs';
import { groupQueryKeys } from '../../../../app/query-keys-factories/groups';
import { GROUP_NAMES_MAPPING } from '../../../../app/constants/groups';
import { EditInvitationGroupsDialog } from './EditInvitationGroupsDialog';
import { toast } from 'react-toastify';
import { invitationQueryKeys } from '../../../../app/query-keys-factories/invitations';
import { parseISO } from 'date-fns';
import { useForm } from 'react-hook-form';
import { SelectController } from '../../../../app/components/SelectController';
import { handleApiError } from '../../../../app/utils/errorhandling';

const PAGE_SIZE = 20;

const EditInvitationGroupsButton = ({ invitation }: { invitation: InvitationRead }) => {
  const [show, setShow] = useState(false);
  return (
    <>
      <Button
        variant="outline-primary"
        className="shadow"
        size="sm"
        title="Edit"
        onClick={() => setShow(true)}
      >
        <BsPersonLock />
      </Button>
      <EditInvitationGroupsDialog
        show={show}
        onClose={() => setShow(false)}
        invitation={invitation}
      />
    </>
  );
};

const DeleteInvitationButton = ({ invitation }: { invitation: InvitationRead }) => {
  const [show, setShow] = useState(false);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async () => {
      await InvitationService.invitationDestroy({ id: invitation.id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(invitationQueryKeys.all());
    },
  });

  const handleDeleteClick = async () => {
    try {
      await mutation.mutateAsync();
      toast.success('Invitation canceled successfully');
      setShow(false);
    } catch (error) {
      handleApiError(error);
    }
  };

  return (
    <>
      <Button
        variant="outline-danger"
        className="shadow"
        size="sm"
        title="Deactivate"
        onClick={() => setShow(true)}
      >
        <BsTrash />
      </Button>
      <Modal show={show} onHide={() => setShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Undo Invitation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to cancel the invitation to {invitation.email}?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShow(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => handleDeleteClick()}>
            Undo Invitation
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const ResendInvitationButton = ({ invitation }: { invitation: InvitationRead }) => {
  const [show, setShow] = useState(false);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async () => {
      await InvitationService.invitationResendUpdate({ id: invitation.id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(invitationQueryKeys.all());
    },
  });

  const handleResendClick = async () => {
    try {
      await mutation.mutateAsync();
      toast.success('Invitation resent successfully');
      setShow(false);
    } catch (error) {
      handleApiError(error);
    }
  };

  return (
    <>
      <Button
        variant="outline-primary"
        className="shadow"
        size="sm"
        title="Resend"
        onClick={() => setShow(true)}
      >
        <BsSendPlus />
      </Button>
      <Modal show={show} onHide={() => setShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Resend Invitation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to resend the invitation to {invitation.email}?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={() => setShow(false)}>
            Back
          </Button>
          <Button variant="primary" onClick={() => handleResendClick()}>
            Resend Invitation
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const columns: ColumnDef<InvitationRead>[] = [
  {
    header: 'Email',
    accessorKey: 'email',
    size: 25,
    minSize: 0,
  },
  {
    header: 'Permissions',
    accessorFn: (row): string =>
      row.group_names.map((name) => GROUP_NAMES_MAPPING[name] || name).join(', '),
    cell: (props) => <div className={styles.lineClamp}>{props.getValue<string>()}</div>,
    size: 25,
    minSize: 0,
  },
  {
    header: 'Invited On',
    accessorFn: (row): string => parseISO(row.created_at).toLocaleDateString(),
    size: 25,
    minSize: 0,
  },
  {
    header: 'Actions',
    cell: (props) => (
      <div className="d-flex justify-content-start gap-2">
        <ResendInvitationButton invitation={props.row.original} />
        <EditInvitationGroupsButton invitation={props.row.original} />
        <DeleteInvitationButton invitation={props.row.original} />
      </div>
    ),
    size: 10,
    minSize: 0,
  },
];

// eslint-disable-next-line sonarjs/cognitive-complexity
export const PendingInvitationsList = () => {
  const [currentPageStr, setCurrentPage] = useSearchParamsState('page', '1');
  const [searchFilter, setSearchFilter] = useSearchParamsState('search', '');
  const [groupNamesIn, setGroupNameInFilter] = useSearchParamsState('group_names__in', [], true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetNameFilter = useCallback(
    debounce(
      (value) => {
        setSearchFilter(value);
      },
      1000,
      { trailing: true }
    ),
    []
  );
  const currentPage = parseInt(currentPageStr);
  const params = {
    limit: PAGE_SIZE,
    offset: (currentPage - 1) * PAGE_SIZE,
    search: searchFilter,
    groupNamesIn: groupNamesIn,
  };

  const [searchFilterState, setSearchFilterState] = useState(searchFilter);

  const queryGroups = useQuery({
    queryKey: groupQueryKeys.list(params as Parameters<typeof GroupService.groupList>[0]),
    queryFn: async () => {
      let groups: Group[] = [];
      const limit = 100;
      let totalCount: number;
      let offset = 0;

      // fetch all facilities considering that data.count is the total number of facilities
      let data = await GroupService.groupList({
        limit,
        offset,
      });
      totalCount = data.count as number;
      groups = groups.concat(data.results || []);
      while (groups.length < totalCount) {
        offset += limit;
        data = await GroupService.groupList({ limit, offset });
        totalCount = data.count as number;
        groups = groups.concat(data.results || []);
      }
      return groups;
    },
    keepPreviousData: true,
  });
  const queryUsers = useQuery({
    queryKey: invitationQueryKeys.list(
      params as Parameters<typeof InvitationService.invitationList>[0]
    ),
    queryFn: async () => {
      return await InvitationService.invitationList(
        params as Parameters<typeof InvitationService.invitationList>[0]
      );
    },
    keepPreviousData: true,
  });

  const table = useReactTable({
    data: queryUsers?.data?.results ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    debouncedSetNameFilter(searchFilterState);
  }, [debouncedSetNameFilter, searchFilterState]);

  const handleChangePage = (page: number) => {
    setCurrentPage(page.toString());
  };

  const renderItems = () => {
    if (queryUsers.isLoading || queryUsers.isFetching) {
      return <SkeletonTableRows />;
    }

    if (queryUsers.isError) {
      return (
        <TableRow>
          <td colSpan={7} className="text-center">
            Error
          </td>
        </TableRow>
      );
    }

    if (queryUsers.data?.count === 0) {
      return (
        <TableRow>
          <td colSpan={4} className="text-center py-5">
            <div className="d-flex flex-column align-items-center gap-2">
              <div className="text-muted">No pending invitations, invite someone</div>
            </div>
          </td>
        </TableRow>
      );
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow key={row.id}>
        {row.getVisibleCells().map((cell) => (
          <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
        ))}
      </TableRow>
    ));
  };

  const { control } = useForm({
    defaultValues: {
      group_names__in: groupNamesIn,
    },
  });

  return (
    <>
      <Row className="justify-content-center" xs={'auto'}>
        <Col>
          <Alert variant="danger" show={queryUsers.isError}>
            <Alert.Heading>Something went wrong</Alert.Heading>
            <p>Please try again later. If the problem persists, please contact us.</p>
          </Alert>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col xs={12} className="d-flex gap-3 justify-content-start align-items-center">
          <Form.Control
            type="text"
            placeholder="Search by name or email"
            value={searchFilterState}
            style={{ maxWidth: 400 }}
            onChange={(e) => {
              setSearchFilterState(e.target.value);
              setCurrentPage('1');
            }}
          />
          <SelectController
            control={control}
            name="group_names__in"
            placeholder="Filter by Permissions"
            options={
              queryGroups?.data?.map(({ code, title }) => ({
                value: code,
                label: title,
              })) || []
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onChange={(selection: any) => {
              // Extract just the values from the selection array
              const values = Array.isArray(selection)
                ? selection.map((option) => option.value)
                : [];
              setGroupNameInFilter(values);
              setCurrentPage('1');
            }}
          />
        </Col>
      </Row>

      <Row className="justify-content-center" xs={12}>
        <Col>
          <Card className="mb-4">
            <div className="table-responsive">
              <table className="table table-striped table-hover align-middle mb-0">
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          style={{
                            width: `${header.getSize()}%`,
                          }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="table-group-divider">{renderItems()}</tbody>
              </table>
            </div>
          </Card>

          <div className="hstack gap-3 justify-content-center mb-4">
            <span>
              {queryUsers.data?.count ?? 0}{' '}
              {queryUsers.data?.count === 1 ? 'invitation' : 'invitations'}
            </span>
            <Pagination
              count={queryUsers.data?.count ?? 0}
              pageSize={PAGE_SIZE}
              currentPage={currentPage}
              siblingCount={2}
              onChangePage={handleChangePage}
            />
          </div>
        </Col>
      </Row>
    </>
  );
};

const TableRow = ({
  children,
  ...props
}: {
  children: React.ReactNode;
  'data-testid'?: string;
}) => <tr {...props}>{children}</tr>;

const SkeletonTableRows = () => {
  return Array(PAGE_SIZE)
    .fill(0)
    .map((_, index) => (
      <TableRow key={index} data-testid={`skeleton-row-${index}`}>
        <td>
          <div className={styles.skeleton} />
        </td>
        <td>
          <div className={styles.skeleton} />
        </td>
        <td>
          <div className={styles.skeleton} />
        </td>
      </TableRow>
    ));
};
